import { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { toast } from "sonner";
import { HelpCircle, Check, CheckCircle2, Wrench, MapPin, Building2, Hash, User, Clock, RefreshCw, AlertCircle } from "lucide-react";
import { StatusBadge, statusLabel } from "../components/stoerung/StatusBadge";
import { Timeline } from "../components/stoerung/Timeline";
import { ActionForm } from "../components/stoerung/ActionForm";
import sample from "../samples/stoerung.sample.json";
import "../components/stoerung/stoerung.css";

const ACTIONS = {
  question: {
    label: "Rückfrage", icon: HelpCircle,
    formTitle: "Rückfrage an die meldende Stelle",
    help: "Die Rückfrage wird per E-Mail an die für den Standort hinterlegte Kontaktadresse gesendet.",
    placeholder: "Welche Strebe genau ist betroffen? Ist der Bereich abgesperrt?",
    required: true, defaultText: null,
    systemText: "Rückfrage an die meldende Stelle gesendet.",
    nextStatus: "in_progress", toast: "Rückfrage gesendet.",
  },
  accept: {
    label: "Angenommen", icon: Check,
    formTitle: "Kurze Nachricht an die meldende Stelle (optional)",
    help: null,
    placeholder: "Ich schaue mir das morgen früh an.",
    required: false, defaultText: "Angenommen – wird zeitnah erledigt.",
    systemText: "Status auf „Angenommen“ gesetzt – meldende Stelle wurde informiert.",
    nextStatus: "in_progress", toast: "Vorgang angenommen.",
  },
  complete: {
    label: "Erledigt", icon: CheckCircle2,
    formTitle: "Abschlussnotiz (optional)",
    help: null,
    placeholder: "Die lose Strebe wurde befestigt und das Klettergerüst geprüft.",
    required: false, defaultText: "Erledigt – vor Ort behoben.",
    systemText: "Vorgang als „Erledigt“ markiert – meldende Stelle wurde benachrichtigt.",
    nextStatus: "completed", toast: "Vorgang als erledigt markiert.",
  },
  request_external_service: {
    label: "Fachfirma beauftragen", icon: Wrench,
    formTitle: "Hinweis an die meldende Stelle",
    help: "Der Vorgang wird zur Beauftragung einer Fachfirma übergeben und ist für den Hausmeister abgeschlossen.",
    placeholder: "Die Schweißnaht ist gerissen und muss durch einen Fachbetrieb instand gesetzt werden.",
    required: false, defaultText: "Kann ich nicht selbst erledigen – bitte Fachfirma beauftragen.",
    systemText: "Vorgang an die meldende Stelle übergeben mit dem Hinweis „Fachfirma beauftragen“.",
    nextStatus: "external_service_required", toast: "Vorgang an die meldende Stelle übergeben.",
  },
};
const ACTION_ORDER = ["question", "accept", "complete", "request_external_service"];

function fmtTime(iso) {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 min-w-0">
    <Icon size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#94A3B8" }} aria-hidden="true" />
    <div className="min-w-0">
      <div className="text-xs" style={{ color: "#94A3B8" }}>{label}</div>
      <div className="text-sm font-semibold" style={{ wordBreak: "break-word" }}>{value}</div>
    </div>
  </div>
);

export default function Stoerung() {
  const { magSession } = useAuth();
  const worker = magSession?.user_name || "Max Mustermann";
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = sessionStorage.getItem("mag_token");

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [live, setLive] = useState("");

  // EDP-Meldung laden: 1) aus Navigations-State (Viewer), 2) via permid+id nachladen
  // (Deep-Link/Reload), 3) Muster-JSON als Demo-Fallback.
  const loadIssue = useCallback(() => {
    setLoading(true); setLoadError(false); setAction(null); setSendError("");
    const stateIssue = location.state?.issue;
    const permid = searchParams.get("permid");
    if (stateIssue) {
      setIssue(JSON.parse(JSON.stringify(stateIssue)));
      setLoading(false);
      return;
    }
    if (permid && id && token) {
      api.get(`/magauth/messages/${permid}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => {
          const found = (data.messages || []).find((m) => String(m.id) === String(id));
          if (found) setIssue(found); else setLoadError(true);
        })
        .catch(() => setLoadError(true))
        .finally(() => setLoading(false));
      return;
    }
    setIssue(JSON.parse(JSON.stringify(sample)));
    setLoading(false);
  }, [id, token, location.state, searchParams]);

  useEffect(() => { loadIssue(); }, [loadIssue]);

  const isClosed = issue && (issue.status === "completed" || issue.status === "external_service_required");

  const chooseAction = (a) => { setAction(a); setMessage(""); setSendError(""); };
  const cancelAction = () => { setAction(null); setMessage(""); setSendError(""); };

  const applyLocally = (a, text) => {
    const cfg = ACTIONS[a];
    const now = new Date().toISOString();
    const finalText = text && text.trim() ? text.trim() : cfg.defaultText;
    const stamp = Date.now();
    const entries = [];
    if (finalText) entries.push({ id: `m-${stamp}`, type: "message", authorType: "worker", authorName: worker, text: finalText, createdAt: now });
    entries.push({ id: `s-${stamp}`, type: "status", authorType: "system", text: cfg.systemText, createdAt: now });
    setIssue((prev) => ({
      ...prev,
      status: cfg.nextStatus,
      assignedToName: prev.assignedToName || worker,
      version: String((parseInt(prev.version || "1", 10) || 1) + 1),
      timeline: [...prev.timeline, ...entries],
    }));
  };

  const submit = async () => {
    const cfg = ACTIONS[action];
    if (cfg.required && !message.trim()) { setSendError("Bitte geben Sie eine Rückfrage ein."); return; }
    setSending(true); setSendError("");
    try {
      if (issue.externalId && token) {
        // Echte EDP-Aktion: Antwort + Status zurueckschreiben
        const { data } = await api.post(
          `/magauth/messages/${encodeURIComponent(issue.externalId)}/action`,
          { action, message },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (data && data.id) setIssue(data);
        else applyLocally(action, message);
      } else {
        // Demo-Fallback (Muster-JSON)
        await new Promise((res) => setTimeout(res, 500));
        applyLocally(action, message);
      }
      toast.success(cfg.toast);
      setLive(`${cfg.toast} Die meldende Stelle wurde informiert.`);
      setAction(null); setMessage("");
    } catch {
      setSendError("Die Aktion konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  const reloadFromConflict = () => { setConflict(false); loadIssue(); };

  return (
    <div data-testid="stoerung-page">
      {/* Kopfbereich (dunkel) */}
      <div style={{ background: "#0F172A", color: "#fff" }}>
        <div className="container-wd py-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#F97316" }}>
              <Wrench size={16} color="#fff" aria-hidden="true" />
            </span>
            <span className="font-extrabold">WhatsDown</span>
            <span className="text-sm" style={{ color: "#94A3B8" }}>· Facility Management</span>
            <span className="ml-auto text-xs px-2 py-1 rounded-md" style={{ background: "rgba(255,255,255,.12)" }} data-testid="stoerung-perspektive">
              Ansicht für: Hausmeister
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold m-0">Ein Vorgang – drei Klicks zur Antwort</h1>
          <p className="text-sm mt-2 mb-0" style={{ color: "#CBD5E1" }}>
            Rückfrage stellen, annehmen, erledigen oder an eine Fachfirma übergeben – jede Aktion wird dokumentiert und an die meldende Stelle gespiegelt.
          </p>
        </div>
      </div>

      <div className="container-wd py-6" style={{ maxWidth: 820 }}>
        {/* Screenreader-Ankündigungen */}
        <div aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }} data-testid="stoerung-live">{live}</div>

        {searchParams.get("permid") && (
          <button onClick={() => navigate(`/meldungen/${searchParams.get("permid")}`)} data-testid="stoerung-back"
            className="btn-secondary mb-4" style={{ padding: "6px 12px", fontSize: 13 }}>
            ← Zurück zu den Meldungen
          </button>
        )}

        {loading && (
          <div className="flex flex-col gap-4" data-testid="stoerung-loading">
            <div className="wd-skeleton" style={{ height: 120 }} />
            <div className="wd-skeleton" style={{ height: 180 }} />
            <div className="wd-skeleton" style={{ height: 90 }} />
          </div>
        )}

        {!loading && loadError && (
          <div className="card-wd p-6 text-center" data-testid="stoerung-load-error">
            <AlertCircle size={28} className="mx-auto mb-2" style={{ color: "#B91C1C" }} />
            <p className="font-bold mb-1">Vorgang konnte nicht geladen werden</p>
            <p className="text-sm mb-4" style={{ color: "#64748B" }}>Bitte versuchen Sie es erneut.</p>
            <button className="btn-primary mx-auto" onClick={loadIssue} data-testid="stoerung-retry"><RefreshCw size={16} /> Erneut laden</button>
          </div>
        )}

        {!loading && !loadError && issue && (
          <div className="flex flex-col gap-6">
            {/* Vorgangskopf */}
            <section className="card-wd p-5" data-testid="stoerung-header">
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs font-bold" style={{ color: "#94A3B8" }}>{issue.referenceNumber}</div>
                  <h2 className="text-xl font-extrabold mt-0.5" style={{ wordBreak: "break-word" }} data-testid="stoerung-title">{issue.title}</h2>
                </div>
                <StatusBadge status={issue.status} size="lg" />
              </div>

              {issue.status === "in_progress" && issue.assignedToName && (
                <p className="text-sm mt-2 mb-0 font-semibold" style={{ color: "#C2410C" }} data-testid="stoerung-assignee">
                  Angenommen von {issue.assignedToName}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <Field icon={MapPin} label="Standort" value={issue.locationName} />
                <Field icon={Building2} label="Organisation" value={issue.organizationName} />
                <Field icon={Hash} label="Vorgangsnummer" value={issue.referenceNumber} />
                <Field icon={User} label="Gemeldet von" value={issue.reporterName} />
                <Field icon={Clock} label="Meldezeitpunkt" value={fmtTime(issue.reportedAt)} />
              </div>

              <div className="mt-4 p-4 rounded-xl" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                <div className="text-xs font-bold mb-1" style={{ color: "#94A3B8" }}>Beschreibung</div>
                <p className="text-sm m-0" style={{ whiteSpace: "pre-wrap" }} data-testid="stoerung-description">{issue.description}</p>
              </div>
            </section>

            {/* Verlauf */}
            <section className="card-wd p-5" data-testid="stoerung-verlauf">
              <h3 className="text-sm font-extrabold mb-3" style={{ color: "#475569" }}>Verlauf</h3>
              <Timeline entries={issue.timeline} />
            </section>

            {/* Aktionen bzw. Abschluss */}
            {isClosed ? (
              <section className="card-wd p-5" style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }} data-testid="stoerung-completion">
                <div className="flex items-center gap-2 font-extrabold" style={{ color: "#15803D" }}>
                  <CheckCircle2 size={20} aria-hidden="true" />
                  {issue.status === "completed" ? "Vorgang abgeschlossen." : "Vorgang an die meldende Stelle übergeben."}
                </div>
                <p className="text-sm mt-1 mb-0" style={{ color: "#166534" }}>
                  {issue.status === "completed"
                    ? "Die meldende Stelle wurde automatisch informiert."
                    : "Die meldende Stelle wurde informiert und kann nun eine Fachfirma beauftragen."}
                </p>
              </section>
            ) : (
              <section data-testid="stoerung-actions">
                <p className="text-sm font-bold mb-3" style={{ color: "#475569" }}>Wie möchten Sie fortfahren?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="group" aria-label="Bearbeitungsaktionen">
                  {ACTION_ORDER.map((a) => {
                    const cfg = ACTIONS[a];
                    const Icon = cfg.icon;
                    const active = action === a;
                    return (
                      <button key={a} type="button" data-testid={`action-${a}`} onClick={() => chooseAction(a)} aria-pressed={active}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-left"
                        style={{ border: `2px solid ${active ? "#F97316" : "#E2E8F0"}`, background: active ? "#FFF7ED" : "#fff", color: active ? "#C2410C" : "#0F172A", cursor: "pointer", minHeight: 56, transition: "border-color .15s ease, background-color .15s ease" }}>
                        <Icon size={18} aria-hidden="true" /> {cfg.label}
                      </button>
                    );
                  })}
                </div>

                {action && (
                  <ActionForm config={ACTIONS[action]} value={message} onChange={setMessage}
                    onCancel={cancelAction} onSubmit={submit} sending={sending} error={sendError} />
                )}
              </section>
            )}
          </div>
        )}
      </div>

      {/* Konflikt-Dialog */}
      {conflict && (
        <div role="dialog" aria-modal="true" aria-labelledby="conflict-title" data-testid="stoerung-conflict"
          className="fixed inset-0 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,.5)", zIndex: 80 }}>
          <div className="card-wd p-6" style={{ maxWidth: 420 }}>
            <div id="conflict-title" className="flex items-center gap-2 font-extrabold mb-2">
              <AlertCircle size={20} color="#C2410C" aria-hidden="true" /> Vorgang wurde geändert
            </div>
            <p className="text-sm" style={{ color: "#475569" }}>
              Dieser Vorgang wurde zwischenzeitlich von einer anderen Person aktualisiert. Bitte laden Sie den aktuellen Stand neu. Ihr eingegebener Text bleibt erhalten.
            </p>
            <div className="flex justify-end gap-3 mt-4">
              <button className="btn-secondary" onClick={() => setConflict(false)} data-testid="conflict-keep">Weiter bearbeiten</button>
              <button className="btn-primary" onClick={reloadFromConflict} data-testid="conflict-reload"><RefreshCw size={16} /> Neu laden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
